import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckCircle2Icon,
  KeyRoundIcon,
  LockIcon,
  MailIcon,
  ShieldCheckIcon,
  TrophyIcon,
  UsersIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../hooks/useAuth";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [view, setView] = useState("login");
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [isSendingReset, setIsSendingReset] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const destination = location.state?.from || "/";

  useEffect(() => {
    if (!loading && user) {
      navigate(destination, { replace: true });
    }
  }, [user, loading, navigate, destination]);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password) {
      setErrorMessage("Fyll i både e-postadress och lösenord.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { error: loginError } =
        await supabase.auth.signInWithPassword({
          email: email.trim().toLowerCase(),
          password,
        });

      if (loginError) {
        console.error("Inloggningen misslyckades:", loginError);

        if (loginError.message?.includes("Email not confirmed")) {
          setErrorMessage(
            "E-postadressen behöver bekräftas innan du kan logga in."
          );
        } else {
          setErrorMessage(
            "Inloggningen misslyckades. Kontrollera e-post och lösenord."
          );
        }

        return;
      }

      navigate(destination, { replace: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPasswordReset = () => {
    setResetEmail(email.trim());
    setResetError("");
    setResetMessage("");
    setView("reset");
  };

  const closePasswordReset = () => {
    setResetError("");
    setResetMessage("");
    setView("login");
  };

  const handleSendResetEmail = async (event) => {
    event.preventDefault();

    if (!resetEmail.trim()) {
      setResetError("Fyll i din e-postadress.");
      return;
    }

    setIsSendingReset(true);
    setResetError("");
    setResetMessage("");

    try {
      const redirectTo = `${window.location.origin}/aterstall-losenord`;

      const { error } = await supabase.auth.resetPasswordForEmail(
        resetEmail.trim().toLowerCase(),
        { redirectTo }
      );

      if (error) {
        console.error("Kunde inte skicka återställningsmejl:", error);
        setResetError(
          "Återställningsmejlet kunde inte skickas. Försök igen om en stund."
        );
        return;
      }

      setResetMessage(
        "Kontrollera din inkorg. Om adressen är registrerad får du ett mejl med en länk för att välja ett nytt lösenord."
      );
    } finally {
      setIsSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f3f0e7] px-4">
        <div className="flex items-center gap-3 rounded-2xl border border-[#d8d2c4] bg-[#fffdf8] px-5 py-4 text-[#1f2d26] shadow-sm">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e5ede7] text-[#183d2d]">
            ⛳
          </span>
          <div>
            <p className="text-sm font-semibold">Laddar HoleInOne</p>
            <p className="text-xs text-[#68716c]">
              Kontrollerar din inloggning…
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isResetView = view === "reset";

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto grid min-h-screen w-full max-w-6xl gap-6 px-4 py-6 sm:px-6 sm:py-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
        <section className="relative hidden overflow-hidden rounded-[28px] bg-[#183d2d] p-8 text-white shadow-[0_22px_65px_rgba(31,45,38,0.16)] lg:flex lg:flex-col lg:p-10">
          <div className="pointer-events-none absolute -right-28 -top-24 h-80 w-80 rounded-full bg-[#b08b45]/22 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-48 -left-24 h-96 w-96 rounded-full bg-white/[0.08] blur-3xl" />

          <div className="relative">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs font-semibold text-white/55 transition hover:text-white"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Till startsidan
            </Link>

            <div className="mt-10 flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10">
                <img
                  src={LOGO_URL}
                  alt="HoleInOne"
                  className="h-8 w-8 object-contain brightness-125"
                />
              </span>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
                  HoleInOne.se
                </p>
                <p className="mt-0.5 text-sm font-medium text-white/80">
                  Tournament manager
                </p>
              </div>
            </div>

            <p className="mt-10 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#dfcc97]">
              Välkommen tillbaka
            </p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight">
              Fortsätt där tävlingen slutade.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-7 text-white/58">
              Hantera startfält, rundor, live scorekort, lagtävlingar och
              resultat från samma konto.
            </p>
          </div>

          <div className="relative mt-10 space-y-3">
            {[
              {
                icon: TrophyIcon,
                title: "Turneringar och resultat",
                text: "Samlad administration före, under och efter rundan.",
              },
              {
                icon: UsersIcon,
                title: "Spelare och lag",
                text: "Återanvänd deltagare och bygg olika tävlingsformat.",
              },
              {
                icon: ShieldCheckIcon,
                title: "Kontrollerad beta",
                text: "Nya konton skapas endast med personlig åtkomstkod.",
              },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.055] p-4"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#dcc78f]">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{item.title}</p>
                    <p className="mt-1 text-xs leading-5 text-white/45">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="relative mt-auto pt-10 text-[10px] text-white/32">
            © {new Date().getFullYear()} HoleInOne.se · Hollander Web
          </p>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.1)]">
          <div className="border-b border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 sm:px-8">
            <div className="flex items-center justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs font-semibold text-[#68716c] transition hover:text-[#183d2d] lg:hidden"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Startsidan
              </Link>

              <div className="ml-auto inline-flex rounded-xl border border-[#d8d2c4] bg-white p-1">
                <span className="inline-flex items-center gap-2 rounded-lg bg-[#183d2d] px-3 py-2 text-[10px] font-semibold text-white">
                  <UsersIcon className="h-3.5 w-3.5" />
                  Privatperson
                </span>
                <Link
                  to="/golfklubb"
                  className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[10px] font-semibold text-[#7b817d] transition hover:bg-[#f4f0e7] hover:text-[#183d2d]"
                >
                  <Building2Icon className="h-3.5 w-3.5" />
                  Golfklubb
                  <span className="hidden rounded-full bg-[#efe7d5] px-1.5 py-0.5 text-[7px] uppercase tracking-[0.1em] text-[#9a7735] sm:inline">
                    Senare
                  </span>
                </Link>
              </div>
            </div>
          </div>

          <div className="px-5 py-7 sm:px-8 sm:py-9">
            <div className="flex items-center gap-3 lg:hidden">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#183d2d]">
                <img
                  src={LOGO_URL}
                  alt="HoleInOne"
                  className="h-7 w-7 object-contain brightness-125"
                />
              </span>
              <div>
                <p className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[#9a7735]">
                  HoleInOne.se
                </p>
                <p className="text-sm font-semibold text-[#183d2d]">
                  Tournament manager
                </p>
              </div>
            </div>

            <div className="mt-7 lg:mt-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                {isResetView ? "Kontoåterställning" : "Privatkonto"}
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-[#183d2d] sm:text-3xl">
                {isResetView ? "Återställ lösenord" : "Logga in"}
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#68716c]">
                {isResetView
                  ? "Ange e-postadressen till kontot så skickar vi en återställningslänk."
                  : "Använd e-postadressen och lösenordet som hör till ditt konto."}
              </p>
            </div>

            {isResetView ? (
              <form onSubmit={handleSendResetEmail} className="mt-7 space-y-4">
                <div>
                  <label
                    htmlFor="reset-email"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    E-postadress
                  </label>
                  <div className="relative">
                    <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                    <input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      placeholder="namn@exempel.se"
                      value={resetEmail}
                      onChange={(event) => setResetEmail(event.target.value)}
                      className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                    />
                  </div>
                </div>

                {resetError && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                  >
                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{resetError}</p>
                  </div>
                )}

                {resetMessage && (
                  <div
                    role="status"
                    className="flex items-start gap-3 rounded-xl border border-[#bfd5c6] bg-[#f2f8f3] px-3.5 py-3 text-sm text-[#287148]"
                  >
                    <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{resetMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSendingReset || Boolean(resetMessage)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <KeyRoundIcon className="h-4 w-4" />
                  {isSendingReset
                    ? "Skickar länk…"
                    : resetMessage
                      ? "Länken är skickad"
                      : "Skicka återställningslänk"}
                </button>

                <button
                  type="button"
                  onClick={closePasswordReset}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#cfc7b8] bg-white px-5 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#f5f1e8]"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Tillbaka till inloggningen
                </button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="mt-7 space-y-4">
                <div>
                  <label
                    htmlFor="login-email"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                  >
                    E-postadress
                  </label>
                  <div className="relative">
                    <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                    <input
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      placeholder="namn@exempel.se"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <label
                      htmlFor="login-password"
                      className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#68716c]"
                    >
                      Lösenord
                    </label>
                    <button
                      type="button"
                      onClick={openPasswordReset}
                      className="text-[10px] font-semibold text-[#183d2d] transition hover:text-[#9a7735] hover:underline"
                    >
                      Glömt lösenord?
                    </button>
                  </div>
                  <div className="relative">
                    <LockIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                    <input
                      id="login-password"
                      type="password"
                      autoComplete="current-password"
                      placeholder="Ditt lösenord"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                  >
                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? "Loggar in…" : "Logga in"}
                  {!isSubmitting && (
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </button>
              </form>
            )}

            {!isResetView && (
              <div className="mt-7 rounded-2xl border border-[#dfd7c8] bg-[#f7f3ea] p-4">
                <p className="text-xs font-semibold text-[#183d2d]">
                  HoleInOne är en privat beta
                </p>
                <p className="mt-1.5 text-xs leading-5 text-[#68716c]">
                  Nya konton kräver en personlig åtkomstkod. Saknar du kod kan
                  du skicka en betaansökan.
                </p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-xs font-semibold">
                  <Link
                    to="/ansok-beta"
                    className="text-[#183d2d] hover:text-[#9a7735] hover:underline"
                  >
                    Ansök om åtkomst
                  </Link>
                  <Link
                    to="/register"
                    className="text-[#183d2d] hover:text-[#9a7735] hover:underline"
                  >
                    Jag har en kod
                  </Link>
                </div>
              </div>
            )}
          </div>

          <footer className="border-t border-[#e4ded1] bg-[#f7f4ec] px-5 py-4 text-center text-[10px] text-[#7b817d]">
            © {new Date().getFullYear()} HoleInOne.se · En tjänst från Hollander Web
          </footer>
        </section>
      </main>
    </div>
  );
}